import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useToast } from '../../hooks/use-toast';
import { Eye, Trash2, Download, FileText } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import api from '../../utils/api';

const ApplicationManagement = ({ onUpdate }) => {
  const [applications, setApplications] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const { toast } = useToast();

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await api.get('/applications');
      setApplications(response.data);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('応募データの取得に失敗しました:', error);
    }
  };

  const handleStatusChange = async (applicationId, newStatus) => {
    try {
      await api.put(`/applications/${applicationId}`, { status: newStatus });
      toast({
        title: 'ステータス更新',
        description: `ステータスを「${newStatus}」に変更しました`,
      });
      fetchApplications();
      if (selectedApplication && selectedApplication.id === applicationId) {
        setSelectedApplication({ ...selectedApplication, status: newStatus });
      }
    } catch (error) {
      toast({
        title: 'エラー',
        description: 'ステータスの更新に失敗しました',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('本当に削除しますか？')) {
      return;
    }

    try {
      await api.delete(`/applications/${id}`);
      toast({
        title: '削除完了',
        description: '応募を削除しました',
      });
      fetchApplications();
      setIsDetailOpen(false);
    } catch (error) {
      toast({
        title: 'エラー',
        description: '削除に失敗しました',
        variant: 'destructive',
      });
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      '未確認': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      '確認済み': 'bg-amber-600/20 text-amber-500 border-amber-600/30',
      '面接調整中': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      '採用': 'bg-green-500/20 text-green-400 border-green-500/30',
      '不採用': 'bg-gray-100/20 text-gray-600 border-gray-300/30',
    };
    return classes[status] || classes['未確認'];
  };

  const filteredApplications = statusFilter === 'all' 
    ? applications 
    : applications.filter(app => app.status === statusFilter);

  return (
    <Card className="bg-gradient-to-br from-gray-50/90 to-gray-100/90 border-amber-500/30 backdrop-blur-sm">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <CardTitle className="text-xl sm:text-2xl bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
            応募管理
          </CardTitle>
          <div className="flex items-center space-x-2 sm:space-x-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 bg-white/50 border-amber-500/30 text-gray-900">
                <SelectValue placeholder="フィルター" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">すべて</SelectItem>
                <SelectItem value="未確認">未確認</SelectItem>
                <SelectItem value="確認済み">確認済み</SelectItem>
                <SelectItem value="面接調整中">面接調整中</SelectItem>
                <SelectItem value="採用">採用</SelectItem>
                <SelectItem value="不採用">不採用</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <div className="space-y-4">
          {filteredApplications.length === 0 ? (
            <p className="text-gray-500 text-center py-8">応募データがありません</p>
          ) : (
            filteredApplications.map((app) => (
              <div
                key={app.id}
                className="p-6 rounded-lg bg-white/50 border border-amber-500/20 hover:border-amber-500/40 transition-all"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{app.name}</h3>
                      <span className={`px-3 py-1 text-xs rounded-full border ${getStatusBadgeClass(app.status)}`}>
                        {app.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">
                      応募先: <span className="text-amber-500">{app.recruitment_title}</span>
                    </p>
                    <p className="text-sm text-gray-600 mb-1">
                      📧 {app.email} | 📞 {app.phone}
                    </p>
                    <p className="text-xs text-gray-500">
                      応募日時: {new Date(app.created_at).toLocaleString('ja-JP')}
                    </p>
                  </div>

                  <div className="flex space-x-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedApplication(app);
                        setIsDetailOpen(true);
                      }}
                      className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Select
                      value={app.status}
                      onValueChange={(newStatus) => handleStatusChange(app.id, newStatus)}
                    >
                      <SelectTrigger className="w-32 h-9 bg-white/50 border-amber-500/30 text-gray-900 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="未確認">未確認</SelectItem>
                        <SelectItem value="確認済み">確認済み</SelectItem>
                        <SelectItem value="面接調整中">面接調整中</SelectItem>
                        <SelectItem value="採用">採用</SelectItem>
                        <SelectItem value="不採用">不採用</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(app.id)}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>

      {/* 詳細ダイアログ */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl bg-white border-amber-500/30 text-gray-900">
          <DialogHeader>
            <DialogTitle className="text-2xl bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
              応募詳細
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              {selectedApplication?.recruitment_title}
            </DialogDescription>
          </DialogHeader>

          {selectedApplication && (
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-gray-600 text-sm">応募者名</Label>
                <p className="text-gray-900 font-semibold mt-1">{selectedApplication.name}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600 text-sm">メールアドレス</Label>
                  <p className="text-gray-900 mt-1">{selectedApplication.email}</p>
                </div>
                <div>
                  <Label className="text-gray-600 text-sm">電話番号</Label>
                  <p className="text-gray-900 mt-1">{selectedApplication.phone}</p>
                </div>
              </div>

              <div>
                <Label className="text-gray-600 text-sm">志望動機・自己PR</Label>
                <p className="text-gray-900 mt-1 whitespace-pre-wrap bg-white/50 p-4 rounded-lg">
                  {selectedApplication.message}
                </p>
              </div>

              {(selectedApplication.resume_url || selectedApplication.cover_letter_url) && (
                <div>
                  <Label className="text-gray-600 text-sm mb-2 block">添付ファイル</Label>
                  <div className="space-y-2">
                    {selectedApplication.resume_url && (
                      <a
                        href={selectedApplication.resume_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-2 text-amber-500 hover:text-amber-500"
                      >
                        <FileText className="w-4 h-4" />
                        <span>履歴書をダウンロード</span>
                        <Download className="w-4 h-4" />
                      </a>
                    )}
                    {selectedApplication.cover_letter_url && (
                      <a
                        href={selectedApplication.cover_letter_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-2 text-amber-500 hover:text-amber-500"
                      >
                        <FileText className="w-4 h-4" />
                        <span>職務経歴書をダウンロード</span>
                        <Download className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-gray-600 text-sm">ステータス</Label>
                <Select
                  value={selectedApplication.status}
                  onValueChange={(newStatus) => handleStatusChange(selectedApplication.id, newStatus)}
                >
                  <SelectTrigger className="w-full mt-1 bg-white/50 border-amber-500/30 text-gray-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="未確認">未確認</SelectItem>
                    <SelectItem value="確認済み">確認済み</SelectItem>
                    <SelectItem value="面接調整中">面接調整中</SelectItem>
                    <SelectItem value="採用">採用</SelectItem>
                    <SelectItem value="不採用">不採用</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="text-xs text-gray-500 pt-4 border-t border-amber-500/20">
                応募日時: {new Date(selectedApplication.created_at).toLocaleString('ja-JP')}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ApplicationManagement;
